import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import "./Dashboard.css";

/**
 * RenameProjectDialog - Modal dialog for renaming a project
 * @param {boolean} open - Whether dialog is open
 * @param {Object} project - The project being renamed
 * @param {Function} onSave - (projectId, newName) => void
 * @param {Function} onClose - Close callback
 * @param {boolean} darkMode - Dark theme flag
 */
export default function RenameProjectDialog({
  open,
  project,
  existingProjects = [],
  onSave,
  onClose,
  darkMode = false,
}) {
  const [name, setName] = useState(project?.name || "");
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setName(project?.name || "");
      setError("");
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, project]);

  if (!open || typeof document === "undefined" || !document.body) return null;

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    if (trimmed === project?.name) {
      onClose?.();
      return;
    }

    // Check for duplicates (case-insensitive), excluding current project
    const isDuplicate = existingProjects.some(
      (p) =>
        p._id !== project?._id && p.name.toLowerCase() === trimmed.toLowerCase(),
    );
    if (isDuplicate) {
      setError("A project with this name already exists");
      return;
    }

    onSave?.(project._id, trimmed);
    onClose?.();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose?.();
    }
  };

  const dialogContent = (
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000000,
        pointerEvents: "none",
      }}
    >
      <motion.div
        role="dialog"
        aria-label="Rename Project"
        aria-modal="true"
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96 }}
        transition={{ duration: 0.15 }}
        style={{
          width: "420px",
          maxWidth: "90vw",
          pointerEvents: "auto",
          backgroundColor: darkMode ? "#1e293b" : "#ffffff",
          border: `1px solid ${darkMode ? "#475569" : "#e5e7eb"}`,
          borderRadius: "0.75rem",
          boxShadow: darkMode
            ? "0 10px 30px rgba(0,0,0,0.6)"
            : "0 10px 30px rgba(0,0,0,0.15)",
          padding: "1.25rem",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: "1rem",
              fontWeight: 700,
              color: darkMode ? "#f1f5f9" : "#0f172a",
            }}
          >
            Rename Project
          </h3>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "0.25rem",
              color: darkMode ? "#94a3b8" : "#64748b",
              display: "flex",
              alignItems: "center",
              borderRadius: "0.25rem",
            }}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          <input
            ref={inputRef}
            id="rename-project-input"
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError("");
            }}
            onKeyDown={handleKeyDown}
            placeholder="Project name"
            maxLength={80}
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "0.625rem 0.75rem",
              fontSize: "0.9375rem",
              border: `1px solid ${error ? "#ef4444" : darkMode ? "#475569" : "#e2e8f0"}`,
              borderRadius: "0.5rem",
              backgroundColor: darkMode ? "#0f172a" : "#f8fafc",
              color: darkMode ? "#f1f5f9" : "#1e293b",
              outline: "none",
              transition: "border-color 0.15s",
            }}
          />
          {error && (
            <p
              style={{
                color: "#ef4444",
                fontSize: "0.8125rem",
                margin: "-0.25rem 0 0 0",
              }}
            >
              {error}
            </p>
          )}
          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: "0.5rem 1.125rem",
                fontSize: "0.875rem",
                border: `1px solid ${darkMode ? "#475569" : "#e2e8f0"}`,
                borderRadius: "0.5rem",
                background: "transparent",
                color: darkMode ? "#cbd5e1" : "#475569",
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim() || name.trim() === project?.name}
              style={{
                padding: "0.5rem 1.125rem",
                fontSize: "0.875rem",
                border: "none",
                borderRadius: "0.5rem",
                background:
                  name.trim() && name.trim() !== project?.name
                    ? "#2563eb"
                    : darkMode
                    ? "#334155"
                    : "#cbd5e1",
                color: "#fff",
                cursor:
                  name.trim() && name.trim() !== project?.name
                    ? "pointer"
                    : "not-allowed",
                fontWeight: 600,
                transition: "background 0.15s",
              }}
            >
              Save
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );

  return createPortal(
    <>
      <div
        role="presentation"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 999999,
          backgroundColor: "rgba(0,0,0,0.25)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
        }}
        onClick={onClose}
        onMouseDown={onClose}
      />
      {dialogContent}
    </>,
    document.body
  );
}
